/* istanbul ignore file */

import { useRef } from 'react';
import { BufferGeometry, Float32BufferAttribute, LineBasicMaterial, LineSegments } from 'three';
import { useFrame } from '@react-three/fiber';
import { GridHint } from './GridHint';

type ModelGridProps = {
  modelSize: { x: number; y: number; z: number };
};

const getCellSize = (longestDimension: number): number => {
  if (longestDimension < 0.5) {
    return 0.1;
  }
  if (longestDimension <= 20) {
    return 1;
  }
  return 10;
};

const getCalculateGridFitToModelSize = (modelMaxDimension: number): number => {
  const minimumGridSizeForDecimalScale = 40;

  if (modelMaxDimension > minimumGridSizeForDecimalScale) {
    return Math.ceil(modelMaxDimension / 10) * 10;
  }
  if (modelMaxDimension < 1) {
    return 0.1;
  }
  return modelMaxDimension;
};

const SimpleGrid = ({ gridSize, cellSize }: { gridSize: number; cellSize: number }) => {
  const lineRef = useRef<LineSegments>(null);
  const positions = [];
  const geometry = new BufferGeometry();
  const gridColor = 'rgb(211, 211, 211)';

  for (let i = -gridSize / 2; i <= gridSize / 2; i += cellSize) {
    positions.push(-gridSize / 2, 0, i, gridSize / 2, 0, i); // Horizontal lines (x, y, z)
    positions.push(i, 0, -gridSize / 2, i, 0, gridSize / 2); // Vertical lines (x, y, z)
  }

  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

  useFrame(() => {
    if (lineRef.current) {
      lineRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return <primitive ref={lineRef} object={new LineSegments(geometry, new LineBasicMaterial({ color: gridColor }))} />;
};

export const ModelGrid = ({ modelSize }: ModelGridProps) => {
  const modelMaxDimension = Math.max(modelSize.x, modelSize.z);
  const gridFitToModelSize = getCalculateGridFitToModelSize(modelMaxDimension);
  const cellSize = getCellSize(modelMaxDimension);
  const gridSizeToRender = gridFitToModelSize + 2 * cellSize;

  return (
    <>
      <mesh rotation={[0, 0, 0]} position={[0, -modelSize.y / 2 - 0.01, 0]}>
        <SimpleGrid gridSize={gridSizeToRender} cellSize={cellSize} />
      </mesh>
      <GridHint gridSize={gridSizeToRender} cellSize={cellSize} modelSize={modelSize.y} />
    </>
  );
};
